import L, { type LayerGroup, type Map as LeafletMap } from "leaflet";
import { useCallback, useEffect, useMemo, useRef } from "react";

import type { MapLineData, MapMovementData } from "./api";
import { displayLocale } from "./localization";
import { lineMidpoint, movementKey, movementPath, type LatLngTuple } from "./trafficGeometry";

export type TrafficMapFlow = {
  id: string;
  entryLineID: string;
  exitLineID: string;
  total: number;
  classCounts: Record<string, number>;
};

export type TrafficMapVisualPoint = {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  total: number;
  classCounts: Record<string, number>;
  lines: MapLineData[];
  movements: MapMovementData[];
  flows: TrafficMapFlow[];
};

const tileURL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const tileAttribution = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

function markerDiameter(value: number, maximum: number) {
  if (value <= 0) return 34;
  return Math.round(34 + Math.sqrt(value / Math.max(1, maximum)) * 42);
}

type ClassSegment = {
  vehicleClass: string;
  start: number;
  colorEnd: number;
  end: number;
  middle: number;
  percentage: number;
};

function classSegments(classCounts: Record<string, number>): ClassSegment[] {
  const entries = Object.entries(classCounts).filter(([, value]) => value > 0);
  const total = entries.reduce((sum, [, value]) => sum + value, 0);
  if (total === 0) return [];

  let cursor = 0;
  return entries.map(([vehicleClass, value]) => {
    const start = cursor;
    const percentage = value / total * 100;
    cursor += percentage;
    const separator = entries.length > 1 ? Math.min(0.65, percentage * 0.18) : 0;
    return {
      vehicleClass,
      start,
      colorEnd: cursor - separator,
      end: cursor,
      middle: start + Math.max(0, percentage - separator) / 2,
      percentage,
    };
  });
}

function classGradient(segments: ClassSegment[], classColors: Record<string, string>) {
  if (segments.length === 0) return "#aeb3b6";
  return `conic-gradient(${segments.map((segment) => (
    `${classColors[segment.vehicleClass] ?? "#5f6368"} ${segment.start}% ${segment.colorEnd}%, #fff ${segment.colorEnd}% ${segment.end}%`
  )).join(", ")})`;
}

function percentageLabel(value: number) {
  if (value > 0 && value < 1) return "<1 %";
  return `${Math.round(value)} %`;
}

function buildMarkerRoot() {
  const root = document.createElement("div");
  root.className = "traffic-map-marker";

  const selection = document.createElement("span");
  selection.className = "traffic-marker-selection";

  const ring = document.createElement("span");
  ring.className = "traffic-marker-ring";

  const value = document.createElement("strong");
  const percentages = document.createElement("span");
  percentages.className = "traffic-marker-percentages";
  percentages.setAttribute("aria-hidden", "true");
  ring.append(value, percentages);
  selection.append(ring);

  const name = document.createElement("span");
  name.className = "traffic-marker-name";
  root.append(selection, name);
  return root;
}

function updateMarkerRoot(
  root: HTMLElement,
  point: TrafficMapVisualPoint,
  maximum: number,
  classColors: Record<string, string>,
  classLabels: Record<string, string>,
  active: boolean,
) {
  const diameter = markerDiameter(point.total, maximum);
  const segments = classSegments(point.classCounts);
  root.classList.toggle("active", active);
  root.style.setProperty("--traffic-marker-size", `${diameter}px`);

  const ring = root.querySelector<HTMLElement>(".traffic-marker-ring");
  const percentages = root.querySelector<HTMLElement>(".traffic-marker-percentages");
  const value = root.querySelector<HTMLElement>("strong");
  const name = root.querySelector<HTMLElement>(".traffic-marker-name");
  if (!ring || !percentages || !value || !name) return diameter;
  ring.style.background = classGradient(segments, classColors);

  const existingLabels = new Map(Array.from(percentages.querySelectorAll<HTMLElement>(".traffic-marker-percentage")).map(
    (label) => [label.dataset.vehicleClass ?? "", label],
  ));
  const visibleClasses = new Set<string>();
  segments.forEach((segment) => {
    const angle = segment.middle / 100 * Math.PI * 2 - Math.PI / 2;
    let label = existingLabels.get(segment.vehicleClass);
    if (!label) {
      label = document.createElement("span");
      label.className = "traffic-marker-percentage";
      label.dataset.vehicleClass = segment.vehicleClass;
      percentages.append(label);
    }
    label.style.setProperty("--traffic-marker-label-x", `${50 + Math.cos(angle) * 42}%`);
    label.style.setProperty("--traffic-marker-label-y", `${50 + Math.sin(angle) * 42}%`);
    label.textContent = percentageLabel(segment.percentage);
    label.title = `${classLabels[segment.vehicleClass] ?? segment.vehicleClass}: ${percentageLabel(segment.percentage)}`;
    visibleClasses.add(segment.vehicleClass);
  });
  existingLabels.forEach((label, vehicleClass) => {
    if (!visibleClasses.has(vehicleClass)) label.remove();
  });

  value.textContent = point.total.toLocaleString(displayLocale);
  name.textContent = point.name;
  return diameter;
}

function markerIcon(
  point: TrafficMapVisualPoint,
  maximum: number,
  classColors: Record<string, string>,
  classLabels: Record<string, string>,
  active: boolean,
) {
  const root = buildMarkerRoot();
  const diameter = updateMarkerRoot(root, point, maximum, classColors, classLabels, active);

  return L.divIcon({
    className: "traffic-map-div-icon",
    html: root,
    iconAnchor: [diameter / 2, diameter / 2],
    iconSize: [diameter, diameter],
  });
}

function updateMarkerElement(
  marker: L.Marker,
  point: TrafficMapVisualPoint,
  maximum: number,
  classColors: Record<string, string>,
  classLabels: Record<string, string>,
  active: boolean,
) {
  const element = marker.getElement();
  const root = element?.querySelector<HTMLElement>(".traffic-map-marker");
  if (!element || !root) return;

  const diameter = updateMarkerRoot(root, point, maximum, classColors, classLabels, active);
  element.style.width = `${diameter}px`;
  element.style.height = `${diameter}px`;
  element.style.marginLeft = `${-diameter / 2}px`;
  element.style.marginTop = `${-diameter / 2}px`;
  element.setAttribute("aria-label", `${point.name}: ${point.total.toLocaleString(displayLocale)} Verkehrsteilnehmende`);
  element.setAttribute("title", point.name);
}

function showPoints(map: LeafletMap, points: TrafficMapVisualPoint[]) {
  if (points.length === 1) {
    map.setView([points[0].latitude, points[0].longitude], 16, { animate: false });
    return;
  }
  map.fitBounds(points.map((point) => [point.latitude, point.longitude] as [number, number]), {
    animate: false,
    maxZoom: 15,
    padding: [54, 54],
  });
}

function arrowHead(path: LatLngTuple[], scale: number): LatLngTuple[] {
  const tip: LatLngTuple = path.at(-1) ?? [0, 0];
  const before: LatLngTuple = path.at(-2) ?? tip;
  const deltaLatitude = tip[0] - before[0];
  const deltaLongitude = tip[1] - before[1];
  const length = Math.hypot(deltaLatitude, deltaLongitude) || 1;
  const unitLatitude = deltaLatitude / length;
  const unitLongitude = deltaLongitude / length;
  const back: LatLngTuple = [tip[0] - unitLatitude * scale, tip[1] - unitLongitude * scale];
  const width = scale * 0.58;
  return [
    tip,
    [back[0] - unitLongitude * width, back[1] + unitLatitude * width],
    [back[0] + unitLongitude * width, back[1] - unitLatitude * width],
  ];
}

function flowGeometry(point: TrafficMapVisualPoint, flow: TrafficMapFlow) {
  const entryLine = point.lines.find((line) => line.id === flow.entryLineID);
  const exitLine = point.lines.find((line) => line.id === flow.exitLineID);
  if (!entryLine || !exitLine) return null;

  const configuredMovement = point.movements.find((movement) => movementKey(movement.entry_line_id, movement.exit_line_id) === movementKey(flow.entryLineID, flow.exitLineID))
    ?? { entry_line_id: flow.entryLineID, exit_line_id: flow.exitLineID, mode: "auto" as const, waypoints: [] };
  const center: LatLngTuple = [point.latitude, point.longitude];
  const entryMidpoint = lineMidpoint(entryLine);
  const exitMidpoint = lineMidpoint(exitLine);
  const path = movementPath(center, point.lines, configuredMovement);
  if (path.length < 2) return null;
  const isUTurn = flow.entryLineID === flow.exitLineID;
  const gateDistance = Math.max(
    Math.hypot(entryMidpoint[0] - center[0], entryMidpoint[1] - center[1]),
    Math.hypot(exitMidpoint[0] - center[0], exitMidpoint[1] - center[1]),
  );
  const arrowScale = Math.min(0.000032, Math.max(0.000014, gateDistance * 0.08));
  return { path, arrow: arrowHead(path, arrowScale), isUTurn };
}

function flowWidth(value: number, maximum: number) {
  return 1.5 + Math.sqrt(value / Math.max(1, maximum)) * 7;
}

function flowDescription(point: TrafficMapVisualPoint, flow: TrafficMapFlow) {
  const names = new Map(point.lines.map((line) => [line.id, line.name]));
  const entry = names.get(flow.entryLineID) ?? flow.entryLineID;
  const exit = names.get(flow.exitLineID) ?? flow.exitLineID;
  const direction = flow.entryLineID === flow.exitLineID ? `${entry}, U-Turn` : `${entry} nach ${exit}`;
  return `${point.name}: ${direction}, ${flow.total.toLocaleString(displayLocale)} Verkehrsteilnehmende`;
}

export function LeafletTrafficMap({ points, maximum, flowMaximum, displayMode, classColors, classLabels, activePointID, onSelectPoint }: {
  points: TrafficMapVisualPoint[];
  maximum: number;
  flowMaximum: number;
  displayMode: "count" | "flow";
  classColors: Record<string, string>;
  classLabels: Record<string, string>;
  activePointID: string;
  onSelectPoint: (id: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const markerLayerRef = useRef<LayerGroup | null>(null);
  const lineLayerRef = useRef<LayerGroup | null>(null);
  const flowLayerRef = useRef<LayerGroup | null>(null);
  const markersRef = useRef(new Map<string, L.Marker>());
  const linesRef = useRef(new Map<string, L.Polyline>());
  const flowsRef = useRef(new Map<string, { path: L.Polyline; arrow: L.Polygon }>());
  const onSelectPointRef = useRef(onSelectPoint);
  const pointSignature = useMemo(() => points.map((point) => `${point.id}:${point.latitude}:${point.longitude}`).join("|"), [points]);

  useEffect(() => {
    onSelectPointRef.current = onSelectPoint;
  }, [onSelectPoint]);

  const resetView = useCallback(() => {
    if (mapRef.current && points.length > 0) showPoints(mapRef.current, points);
  }, [points]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const map = L.map(container, {
      attributionControl: true,
      center: [points[0].latitude, points[0].longitude],
      dragging: true,
      maxZoom: 18,
      minZoom: 11,
      scrollWheelZoom: true,
      touchZoom: true,
      zoom: points.length > 1 ? 13 : 16,
      zoomControl: false,
    });
    L.control.zoom({ position: "topleft" }).addTo(map);
    L.tileLayer(tileURL, {
      attribution: tileAttribution,
      keepBuffer: 0,
      maxZoom: 18,
      referrerPolicy: "strict-origin-when-cross-origin",
      updateWhenIdle: true,
      updateWhenZooming: false,
    }).addTo(map);
    const flowLayer = L.layerGroup().addTo(map);
    const lineLayer = L.layerGroup().addTo(map);
    const markerLayer = L.layerGroup().addTo(map);
    const markers = markersRef.current;
    const lines = linesRef.current;
    const flows = flowsRef.current;
    mapRef.current = map;
    markerLayerRef.current = markerLayer;
    lineLayerRef.current = lineLayer;
    flowLayerRef.current = flowLayer;
    showPoints(map, points);

    const resizeObserver = new ResizeObserver(() => map.invalidateSize({ animate: false }));
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      markers.clear();
      lines.clear();
      flows.clear();
      markerLayerRef.current = null;
      lineLayerRef.current = null;
      flowLayerRef.current = null;
      mapRef.current = null;
      map.remove();
    };
  // The map instance owns its initial options. Data updates are handled by the effects below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const layer = markerLayerRef.current;
    if (!layer) return;
    if (displayMode !== "count") {
      markersRef.current.forEach((marker) => { marker.off(); layer.removeLayer(marker); });
      markersRef.current.clear();
      return;
    }
    // Preserve each Leaflet element so playback updates do not restart its hover/focus transition.
    const visiblePointIDs = new Set(points.map((point) => point.id));
    markersRef.current.forEach((marker, pointID) => {
      if (!visiblePointIDs.has(pointID)) {
        marker.off();
        layer.removeLayer(marker);
        markersRef.current.delete(pointID);
      }
    });

    points.forEach((point) => {
      let marker = markersRef.current.get(point.id);
      if (!marker) {
        marker = L.marker([point.latitude, point.longitude], {
          alt: `${point.name}: ${point.total.toLocaleString(displayLocale)} Verkehrsteilnehmende`,
          icon: markerIcon(point, maximum, classColors, classLabels, point.id === activePointID),
          keyboard: true,
          riseOnHover: true,
          title: point.name,
        });
        marker.on("click", () => onSelectPointRef.current(point.id));
        marker.addTo(layer);
        markersRef.current.set(point.id, marker);
      } else {
        const position = marker.getLatLng();
        if (position.lat !== point.latitude || position.lng !== point.longitude) {
          marker.setLatLng([point.latitude, point.longitude]);
        }
      }
      updateMarkerElement(marker, point, maximum, classColors, classLabels, point.id === activePointID);
    });
  }, [activePointID, classColors, classLabels, displayMode, maximum, points]);

  useEffect(() => {
    const lineLayer = lineLayerRef.current;
    const flowLayer = flowLayerRef.current;
    if (!lineLayer || !flowLayer) return;
    if (displayMode !== "flow") {
      linesRef.current.forEach((line) => lineLayer.removeLayer(line));
      flowsRef.current.forEach(({ path, arrow }) => { path.off(); arrow.off(); flowLayer.removeLayer(path); flowLayer.removeLayer(arrow); });
      linesRef.current.clear();
      flowsRef.current.clear();
      return;
    }

    const visibleLines = new Set<string>();
    const visibleFlows = new Set<string>();
    points.forEach((point) => {
      point.lines.forEach((line) => {
        const key = `${point.id}:${line.id}`;
        visibleLines.add(key);
        let layer = linesRef.current.get(key);
        const positions = line.geometry.coordinates.map(([longitude, latitude]) => [latitude, longitude] as LatLngTuple);
        if (!layer) {
          layer = L.polyline(positions, { className: "traffic-gate-line", color: "#41484c", dashArray: "4 3", interactive: true, opacity: 0.88, weight: 3 });
          layer.bindTooltip(`${point.name} · ${line.name}`, { direction: "top", sticky: true });
          layer.addTo(lineLayer);
          linesRef.current.set(key, layer);
        } else {
          layer.setLatLngs(positions);
        }
      });

      point.flows.forEach((flow) => {
        const geometry = flowGeometry(point, flow);
        if (!geometry || flow.total <= 0) return;
        const key = `${point.id}:${flow.id}`;
        visibleFlows.add(key);
        const width = flowWidth(flow.total, flowMaximum);
        const color = point.id === activePointID ? "#2e6684" : "#5c7d8d";
        const description = flowDescription(point, flow);
        let layers = flowsRef.current.get(key);
        if (!layers) {
          const path = L.polyline(geometry.path, { className: `traffic-flow-path${geometry.isUTurn ? " traffic-flow-path--uturn" : ""}`, color, interactive: true, opacity: 0.82, weight: width });
          const arrow = L.polygon(geometry.arrow, { className: "traffic-flow-arrow", color, fillColor: color, fillOpacity: 0.96, interactive: true, opacity: 0.96, weight: 1 });
          path.bindTooltip(description, { direction: "top", sticky: true });
          arrow.bindTooltip(description, { direction: "top", sticky: true });
          path.on("click", () => onSelectPointRef.current(point.id));
          arrow.on("click", () => onSelectPointRef.current(point.id));
          path.addTo(flowLayer);
          arrow.addTo(flowLayer);
          layers = { path, arrow };
          flowsRef.current.set(key, layers);
        } else {
          layers.path.setLatLngs(geometry.path);
          layers.arrow.setLatLngs(geometry.arrow);
          layers.path.setTooltipContent(description);
          layers.arrow.setTooltipContent(description);
        }
        layers.path.setStyle({ color, opacity: 0.82, weight: width });
        layers.arrow.setStyle({ color, fillColor: color, fillOpacity: 0.96, opacity: 0.96 });
        layers.path.getElement()?.setAttribute("aria-label", description);
        layers.arrow.getElement()?.setAttribute("aria-label", description);
      });
    });

    linesRef.current.forEach((line, key) => {
      if (!visibleLines.has(key)) { lineLayer.removeLayer(line); linesRef.current.delete(key); }
    });
    flowsRef.current.forEach(({ path, arrow }, key) => {
      if (!visibleFlows.has(key)) {
        path.off(); arrow.off(); flowLayer.removeLayer(path); flowLayer.removeLayer(arrow); flowsRef.current.delete(key);
      }
    });
  }, [activePointID, displayMode, flowMaximum, points]);

  useEffect(() => {
    if (mapRef.current) showPoints(mapRef.current, points);
  // Recenter only when the available measurement points change, never during playback.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointSignature]);

  const legendValues = maximum > 0
    ? [0.1, 0.5, 1].map((factor) => Math.max(1, Math.round(maximum * factor)))
    : [0];
  const flowLegendValues = flowMaximum > 0
    ? Array.from(new Set([0.25, 0.55, 1].map((factor) => Math.max(1, Math.round(flowMaximum * factor)))))
    : [];
  const hasLineCatalog = points.some((point) => point.lines.length >= 2);

  return <div className="osm-map-shell">
    <div ref={containerRef} className="osm-map" aria-label="Interaktive OpenStreetMap-Karte der Messstellen" />
    <button type="button" className="map-reset" onClick={resetView}>Auf Messstellen zentrieren</button>
    <div className={`map-symbol-legend${displayMode === "flow" ? " map-flow-legend" : ""}`} aria-label="Legende der Verkehrssymbole">
      <strong>{displayMode === "flow" ? "Gerichtete Verkehrsflüsse" : "Kreisgröße je Messintervall"}</strong>
      {displayMode === "count" ? <div>{legendValues.map((value, index) => {
        const size = Math.round(markerDiameter(value, maximum) * 0.36);
        return <span key={`${value}:${index}`}><i style={{ width: size, height: size }} />{value.toLocaleString(displayLocale)}</span>;
      })}</div> : flowMaximum > 0 ? <div>{flowLegendValues.map((value) => {
        return <span key={value}><i style={{ height: `${flowWidth(value, flowMaximum)}px` }} />{value.toLocaleString(displayLocale)}</span>;
      })}</div> : <div><span>{hasLineCatalog ? "Keine gerichteten Werte im aktuellen Abschnitt" : "Keine Linienkonfiguration verfügbar"}</span></div>}
      <small>{displayMode === "flow" ? "Pfeildicke: Anzahl · Pfeilspitze: Richtung · Schleife: U-Turn" : "Werte je Messstelle · Summe der ausgewählten Verkehrsmodi · Skala für den geladenen Zeitraum"}</small>
    </div>
  </div>;
}
